import { Configuration, PopupRequest } from "@azure/msal-browser";
import { PrivateLambdaService } from "../services/privateLambdaService";
import { getSafeErrorInfo } from "../utils/safeError";

const privateLambdaService = new PrivateLambdaService();

/**
 * Default MSAL configuration (fallback values)
 */
const defaultMsalConfig: Configuration = {
  auth: {
    clientId: "",
    authority: "",
    redirectUri: "",
    postLogoutRedirectUri: "",
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0:
            console.error(message);
            return;
          case 1:
            console.warn(message);
            return;
          case 2:
            console.info(message);
            return;
          case 3:
            console.debug(message);
            return;
        }
      },
    },
  },
};

/**
 * Mutable MSAL configuration that gets updated from backend
 */
export let msalConfig: Configuration = { ...defaultMsalConfig };

/**
 * Fetch MSAL configuration from backend via privateLambdaService
 * Updates the msalConfig object in place
 */
export const fetchAndUpdateMsalConfig = async (): Promise<Configuration> => {
  try {
    console.log('Fetching MSAL configuration from backend via IAM-authenticated Lambda...');
    const config = await privateLambdaService.callPrivateLambdaApi('/api/secrets/msal', 'GET');
    console.log('MSAL configuration fetched successfully:', config);
    
    // Validate required fields
    if (!config.clientId || !config.authority || !config.redirectUri) {
      console.warn('Incomplete MSAL config received from backend:', config);
      throw new Error('Incomplete MSAL configuration');
    }
    
    // Update the configuration object
    msalConfig = {
      ...defaultMsalConfig,
      auth: {
        ...defaultMsalConfig.auth,
        clientId: config.clientId,
        authority: config.authority,
        redirectUri: config.redirectUri,
        postLogoutRedirectUri: config.postLogoutRedirectUri || config.redirectUri,
      },
    };
    
    console.log('MSAL config initialized:', msalConfig);
    return msalConfig;
  } catch (error) {
    const { name, code, message, response } = getSafeErrorInfo(error);
    const status = typeof response?.status === 'number' ? response.status : undefined;
    const statusText = typeof response?.statusText === 'string' ? response.statusText : undefined;

    console.error('Failed to fetch MSAL config from backend:', {
      name,
      code,
      status,
      statusText,
      message
    });
    // Return a minimal config that won't cause URL errors but will be non-functional
    // This allows credential-based login to work even if Microsoft SSO is unavailable
    return {
      ...defaultMsalConfig,
      auth: {
        clientId: "00000000-0000-0000-0000-000000000000",
        authority: "https://login.microsoftonline.com/common",
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
      },
    };
  }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest: PopupRequest = {
  scopes: ["User.Read"], // Request basic user profile information
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API.
 * For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};
